import { GetUserResponse } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import {
    getFallbackErrorKey,
    getImageRejectionCode,
    isApiError
} from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { addOrUpdateCompanyUsersCache } from '../queries/company-cache';

/** The member whose photo is being replaced, and the file to put there. */
type UploadUserImageVariables = {
    companyUserId: string;
    file: File;
};

type UseUploadCompanyUserImageActionData = {
    uploadImage: (
        companyUserId: string,
        file: File
    ) => Promise<GetUserResponse>;
    loading: boolean;
    error: string;
};

/**
 * Replacing a member's photo. The file is rejected by the server rather than
 * the client, so the refusals it can give back are spelled out here.
 *
 * Carries no `isAllowed` of its own: whether the viewer may edit this member is
 * `useEditProfileAction`, which gates the whole page of sections.
 */
export function useUploadCompanyUserImageAction(): UseUploadCompanyUserImageActionData {
    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/users/{id}/image` declares gets a branch, plus the rejection
    // codes the endpoint sends but does not declare.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            switch (getImageRejectionCode(error)) {
                case 'UNSUPPORTED_FILE_FORMAT':
                    return t('shared.errors.unsupported_file_format');
                case 'UNSUPPORTED_FILE_SIZE':
                    return t('shared.errors.unsupported_file_size');
                case 'UNSUPPORTED_IMAGE_DIMENSIONS':
                    return t('shared.errors.unsupported_image_dimensions');
            }

            if (isApiError(error, 'CANNOT_PROCESS_FILE')) {
                return t('shared.errors.cannot_process_file');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: ({ companyUserId, file }: UploadUserImageVariables) =>
            openApiRef.current.users
                .uploadUserImage(companyUserId, { file })
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateCompanyUsersCache(queryClient, data.data.user);

            UIToast.success(t('actions.upload_member_image.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        uploadImage: (
            companyUserId: string,
            file: File
        ): Promise<GetUserResponse> =>
            mutation.mutateAsync({ companyUserId, file }),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
